import React, { Fragment, memo } from 'react';
import { connect } from 'react-redux';
import T from 'prop-types';
import moment from 'moment';
import { get, isEmpty } from 'lodash';
import { ROW_TYPES } from 'workflow/constants/newInbox';
import Description from '../Description/Description';
import { Box } from '@material-ui/core';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import styles from './DetailRows.scss';

const formatDate = (date, timezone) => {
    let format;
    if (timezone) {
        format = 'DD MMMM YYYY hh:mm A';
        return moment.utc(date).utcOffset(timezone).format(format);
    } else {
        format = 'DD MMMM YYYY';
        return moment.utc(date).format(format);
    }
};

// Deprecated, use either DateTime row or DateNoTzRow
const DateRow = ({ label, value, timezone }) => {
    return (
        <Box display='flex' flexDirection='row' className={styles.taskdetailV4}>
            <Box flex='0.3' className={styles.taskLabel}>
                {label}
            </Box>
            <Box flex='0.7'>
                {value && formatDate(value, timezone)}
            </Box>
        </Box>
    );
};

// Displays a date with a time used for showing actions at a specific time and in a specific timezone
const DateTimeRow = ({ label, value, timezone }) => {
    return (
        <Box display='flex' flexDirection='row' className={styles.taskdetailV4}>
            <Box flex='0.3' className={styles.taskLabel}>
                {label}
            </Box>
            <Box flex='0.7'>
                {value && formatDate(value, timezone)}
            </Box>
        </Box>
    );
};

// Displays a date as a recorded date for a log, no timezone is applied to the date and it is shown in the standard date format
const DateNoTzRow = ({ label, value }) => {
    return (
        <Box display='flex' flexDirection='row' className={styles.taskdetailV4}>
            <Box flex='0.3' className={styles.taskLabel}>
                {label}
            </Box>
            <Box flex='0.7'>
                {value && formatDate(value)}
            </Box>
        </Box>
    );
};

const TextRow = ({ label, value, isLink, bindersStudiesInfo, isMobileView }) => {
    const linkObj = bindersStudiesInfo && bindersStudiesInfo.find(s => s.name === value);
    return (
        <Box display='flex' flexDirection='row' className={styles.taskdetailV4}>
            <Box flex='0.3' className={styles.taskLabel}>
                {label}
            </Box>
            <Box flex='0.7'>
                {isLink && linkObj && !isMobileView ? (
                    linkObj.isStudy ? (
                        <div className={styles.textRowLink}>
                            <Link to={`/study/${linkObj.id}`} target="_blank" rel="noopener noreferrer">{value}</Link>
                            <FontAwesomeIcon icon={faExternalLink} style={{ paddingLeft: '5px' }} />
                        </div>
                    ) : (
                        <div className={styles.textRowLink}>
                            <Link to={`/binder/${linkObj.id}/files`} target="_blank" rel="noopener noreferrer">{value}</Link>
                            <FontAwesomeIcon icon={faExternalLink} style={{ paddingLeft: '5px' }} />
                        </div>
                    )
                ) : (
                    value
                )}
            </Box>
        </Box>
    );
};

const RichTextRow = ({ label, value = '{}' }) => {
    const content = JSON.parse(value);
    const hasContent = (content && content.blocks) ? !!get(content, 'blocks[0].text', '') : false;
    return (
        <Box display='flex' flexDirection='row' className={styles.taskdetailV4}>
            <Box flex='0.3' className={styles.taskLabel}>
            </Box>
            {hasContent && <Box flex='0.7'>
                <Description content={content} />
            </Box>}
        </Box>
    );
};

const ListRow = ({ label, value = [], childField }) => {
    const valueRender = () => {
        if (!isEmpty(value) && childField) {
            return value.map(child => get(child, childField, ''));
        }
        if (!childField) {
            return [`(${value.length})`];
        }
        return [''];
    };

    return (
        <Box display='flex' flexDirection='row' className={styles.taskdetailV4}>
            <Box flex='0.3' className={styles.taskLabel}>
                {label}
            </Box>
            <Box flex='0.7'>
                {valueRender().map((data, idx) => data && <div key={label + idx} style={{ paddingTop: '10px' }}>{data}</div>)}
            </Box>
        </Box>
    );
};

const DetailRowsv4 = memo(({ data, configs, timezone, taskType, bindersStudiesInfo, isMobileView }) => {
    return (
        <Fragment>
            <Box className={`${styles.SDFARequestV4Container} ${data && data.typeName === 'task' && styles.taskDetailBorder}`}>
                {configs.map(config => {
                    const { label, type, field, ...rest } = config;
                    const value = config.value || get(data, field);
                    switch (type) {
                        case ROW_TYPES.DATE: {
                            return <DateRow key={field + label} label={label} value={value} timezone={timezone} />;
                        }
                        case ROW_TYPES.DATETIME: {
                            return <DateTimeRow key={field + label} label={label} time={true} value={value} timezone={timezone} />;
                        }
                        case ROW_TYPES.DATE_NO_TZ: {
                            return <DateNoTzRow key={field + label} label={label} value={value} />;
                        }
                        case ROW_TYPES.RICH_TEXT: {
                            return <RichTextRow key={field} label={label} value={value} />;
                        }
                        case ROW_TYPES.LIST: {
                            return <ListRow key={field} label={label} value={value} {...rest} />;
                        }
                        case ROW_TYPES.LINK: {
                            return <TextRow key={field + label} label={label} value={value} isLink={true} bindersStudiesInfo={bindersStudiesInfo} isMobileView={isMobileView} />;
                        }
                        default: {
                            return <TextRow key={field + label} label={label} value={value} />;
                        }
                    }
                })}
            </Box>
        </Fragment>
    );
});

const RowShape = {
    label: T.string,
    value: T.oneOfType([
        T.string,
        T.array
    ]),
    isMobileView: T.bool
};

DateRow.propTypes = { ...RowShape };

TextRow.propTypes = { ...RowShape };

RichTextRow.propTypes = { ...RowShape };

ListRow.propTypes = { ...RowShape };

DetailRowsv4.propTypes = {
    data: T.object,
    configs: T.arrayOf(T.shape({
        label: T.string,
        field: T.string,
        type: T.oneOf(Object.values(ROW_TYPES))
    })),
    isMobileView: T.bool
};

export { DateRow, DateNoTzRow, TextRow, RichTextRow, ListRow };

export default connect((state, props) => {
    const institutionTimezone = props.timezone || get(state, 'me.institutionTimezone', 'Z');
    const bindersStudiesInfo = get(state, 'dashboard.binderStudiesTasks', []);

    return {
        timezone: institutionTimezone,
        bindersStudiesInfo,
    };
})(DetailRowsv4);
